import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { services } from '../app';
import { LESSONS, Lesson, LessonCtx, Step, Target } from '../data/lessons';
import { QuizQ, buildQuiz, darkFirst, learnProgress, lessonContext, lessonId, markLesson, quizHard, recordQuiz, resolve } from '../live/learn';
import { NewsBundle, loadNews } from '../live/news';
import { NEWS_REMINDER } from '../data/islam';
import { Reminder } from './Reminder';
import { PLANETS } from '../live/logbook';
import { seenIndex } from '../live/nights';
import { Vec3, enuToAltAz, refractEnu } from '../lib/math';
import { Settings, hideLabels, huntReticle, panel, place, selection, settings, viewMode } from '../state';
import { Sheet } from './chrome';
import { GalleryStrip } from './GalleryPanel';
import { fmtTime } from './format';
import { Icon } from './icons';
import { openIslam } from './IslamPanel';
import { Quotes } from './Quote';

// Learn: short lessons that point at the real sky, and quizzes made from tonight's sky.

export function LearnPanel() {
  const prog = learnProgress.value;
  const north = place.value.lat > 0;
  const lessons = LESSONS.filter((l) => !l.north || north);
  const start = (id: string) => {
    lessonId.value = id;
    selection.value = null;
    panel.value = 'lesson';
  };
  const quiz = (hard: boolean) => {
    quizHard.value = hard;
    selection.value = null;
    panel.value = 'quiz';
  };
  return (
    <Sheet onClose={() => (panel.value = null)} size="tall" label="Learn">
      <h2 class="sheet-title">Learn</h2>
      <p class="sheet-lead">The finest pictures of space, short lessons that use tonight’s real sky, and a quiz about what is up right now.</p>
      <GalleryStrip />
      <div class="card wide quiz-card">
        <div class="card-title"><Icon name="target" size={16} /> Tonight’s sky quiz</div>
        <div class="card-sub">Eight questions: name what is in the circle, planet or star, which way to face, find it, and true or false.{prog.bestQuiz ? ` Best score: ${prog.bestQuiz} of 8.` : ''}</div>
        <div class="tonight-actions">
          <button class="btn tour-btn" onClick={() => quiz(false)}>Start</button>
          <button class="btn-ghost tour-btn" onClick={() => quiz(true)}>Harder</button>
        </div>
      </div>
      <SkyLog />
      <Discover />
      <h3 class="section-title">Lessons</h3>
      <div class="results">
        {lessons.map((l) => (
          <button class="result" key={l.id} onClick={() => start(l.id)}>
            <span class="result-icon"><Icon name={prog.done[l.id] ? 'check' : 'play'} size={18} /></span>
            <span class="result-text">
              <span class="result-label">{l.title}</span>
              <span class="result-sub">{l.summary} · {l.minutes} min{l.level !== 'Everyone' ? ` · ${l.level}` : ''}</span>
            </span>
            <Icon name="chevron" size={16} />
          </button>
        ))}
      </div>
      <p class="fine">Lessons are written for Ahillah. Numbers that change, such as the Moon’s phase, planets, passes and dates, are worked out live for {place.value.name}.</p>
    </Sheet>
  );
}

const altAz = (v: Vec3) => enuToAltAz(refractEnu(v));
const textOf = (t: string | ((c: LessonCtx) => string), c: LessonCtx) => (typeof t === 'function' ? t(c) : t);
const targetOf = (t: Target | ((c: LessonCtx) => Target | null) | null, c: LessonCtx) => (typeof t === 'function' ? t(c) : t);

/** Frame a direction in the sky left visible by the lesson card (in pointing mode the phone decides). */
function fly(enu: Vec3 | null, fov = 60) {
  const e = services.peek()!.engine;
  if (!enu || viewMode.peek() !== 'manual') return;
  // Once the card has laid out, so its size is known.
  setTimeout(() => {
    const W = window.innerWidth, H = window.innerHeight;
    const card = document.querySelector<HTMLElement>('.tour');
    const r = !card
      ? { x0: 0, y0: 64, x1: W, y1: H - 110 }
      : card.offsetWidth < W - 120
        ? { x0: 0, y0: 64, x1: card.offsetLeft - 8, y1: H - 16 }
        : { x0: 0, y0: 64, x1: W, y1: card.offsetTop - 8 };
    e.frameInView(enu, r, fov, 1.4, -25); // the card hides the ground, so keep the lesson's zoom even for low targets
  }, 60);
}

/** Watch for a target sitting inside the centre circle for most of a second. */
function useFound(enu: (() => Vec3 | null) | null, radius: number, onFound: () => void) {
  const since = useRef(0);
  useEffect(() => {
    if (!enu) return;
    since.current = 0;
    const e = services.peek()!.engine;
    const id = setInterval(() => {
      const v = enu();
      if (!v) return;
      const c = e.camera.forward();
      const off = (Math.acos(Math.max(-1, Math.min(1, c[0] * v[0] + c[1] * v[1] + c[2] * v[2]))) * 180) / Math.PI;
      if (off < radius) {
        if (!since.current) since.current = Date.now();
        else if (Date.now() - since.current > 900) {
          clearInterval(id);
          navigator.vibrate?.(60);
          onFound();
        }
      } else since.current = 0;
    }, 150);
    return () => clearInterval(id);
  }, [enu]);
}

function Choices({ options, answer, picked, onPick }: { options: string[]; answer: number; picked: number | null; onPick: (i: number) => void }) {
  return (
    <div class="choices" role="group">
      {options.map((o, k) => (
        <button
          key={o}
          class={`choice ${picked === null ? '' : k === answer ? 'right' : k === picked ? 'wrong' : 'faded'}`}
          disabled={picked !== null}
          onClick={() => onPick(k)}
        >
          {o}
          {picked !== null && k === answer && <Icon name="check" size={14} />}
        </button>
      ))}
    </div>
  );
}

export function LessonPlayer() {
  const lesson = LESSONS.find((l) => l.id === lessonId.value) as Lesson;
  const [ctx, setCtx] = useState<LessonCtx | null>(null);
  const [i, setI] = useState(0);
  const [picked, setPicked] = useState<number | null>(null);
  const [found, setFound] = useState(false);
  const saved = useRef<Partial<Settings>>({});
  const [jumped, setJumped] = useState<Date | null>(null);

  useEffect(() => {
    let alive = true;
    // Lessons that point at the sky need it dark; the Sun lesson is words only.
    const needsSky = lesson?.steps.some((st) => ('look' in st && st.look) || 'find' in st);
    void (needsSky ? darkFirst() : Promise.resolve(null)).then(async (t) => {
      const c = await lessonContext();
      if (!alive) return;
      setJumped(t);
      setCtx(c);
    });
    return () => {
      alive = false;
      restoreLayers();
      huntReticle.value = false;
      selection.value = null;
    };
  }, []);
  const restoreLayers = () => {
    if (Object.keys(saved.current).length) settings.value = { ...settings.value, ...saved.current };
    saved.current = {};
  };

  const step: Step | undefined = lesson?.steps[i];
  // Each step: point the view, highlight the target, switch on any layer it needs.
  useEffect(() => {
    if (!ctx || !step) return;
    setPicked(null);
    setFound(false);
    restoreLayers();
    huntReticle.value = 'find' in step;
    if ('look' in step || 'find' in step) {
      const t = targetOf('look' in step ? step.look : step.find, ctx);
      const r = t ? resolve(t) : null;
      selection.value = r?.sel ?? null;
      if ('look' in step) {
        if (step.layers) {
          const set = settings.peek() as unknown as Record<string, unknown>;
          saved.current = Object.fromEntries(Object.keys(step.layers).map((k) => [k, set[k]])) as Partial<Settings>;
          settings.value = { ...settings.peek(), ...step.layers } as Settings;
        }
        fly(r?.enu ?? null, step.fov);
      }
    } else selection.value = null;
  }, [ctx, i]);

  const findEnu = useMemo(() => {
    const t = ctx && step && 'find' in step ? targetOf(step.find, ctx) : null;
    return t ? () => resolve(t).enu : null;
  }, [ctx, i]);
  useFound(findEnu && !found ? findEnu : null, ('find' in (step ?? {}) && (step as { radius?: number }).radius) || 3.5, () => setFound(true));

  const close = () => (panel.value = 'learn');
  if (!lesson) return null;
  if (!ctx || !step) {
    return (
      <div class="tour">
        <div class="working"><div class="spinner" /><p>Getting the sky ready…</p></div>
      </div>
    );
  }
  const last = i === lesson.steps.length - 1;
  const next = () => {
    if (last) {
      markLesson(lesson.id);
      close();
    } else setI(i + 1);
  };
  let body;
  let canGo = true;
  if ('ask' in step) {
    canGo = picked !== null;
    body = (
      <>
        <h3>{textOf(step.ask, ctx)}</h3>
        <Choices options={step.options} answer={step.answer} picked={picked} onPick={setPicked} />
        {picked !== null && <p aria-live="polite">{picked === step.answer ? 'Yes! ' : 'Not quite. '}{textOf(step.explain, ctx)}</p>}
      </>
    );
  } else if ('find' in step) {
    const enu = findEnu?.();
    const below = !enu || altAz(enu).alt < 0;
    body = (
      <>
        <h3>{found ? 'Found it' : step.title ?? 'Find it'}</h3>
        <p aria-live="polite">
          {below
            ? 'It is below the horizon right now, so skip this one for tonight.'
            : found
              ? 'Well done.'
              : `${textOf(step.text, ctx)} ${viewMode.value === 'ar' ? 'Point your phone until it sits there.' : 'Drag the sky until it sits there.'}`}
        </p>
      </>
    );
  } else {
    const t = targetOf(step.look, ctx);
    const r = t ? resolve(t) : null;
    const below = r?.enu && !('dir' in (t as object)) ? altAz(r.enu).alt < 0 : false;
    body = (
      <>
        <h3>{step.title ?? lesson.title}</h3>
        {textOf(step.text, ctx) && <p>{textOf(step.text, ctx)}</p>}
        {below && <p class="muted small">It is below the horizon right now, so the ground hides it.</p>}
        {step.quotes && <div class="lesson-quotes"><Quotes ids={step.quotes} /></div>}
        {step.islam && <button class="reminder-more" onClick={() => { if (i === lesson.steps.length - 1) markLesson(lesson.id); close(); openIslam(step.islam); }}>Read the article <Icon name="chevron" size={13} /></button>}
      </>
    );
  }
  return (
    <div class="tour lesson" role="dialog" aria-label={lesson.title}>
      <div class="tour-top">
        <span class="tour-step tnum">{lesson.title} · {i + 1} / {lesson.steps.length}</span>
        <button class="icon-btn" aria-label="Close the lesson" onClick={close}><Icon name="close" size={18} /></button>
      </div>
      {i === 0 && jumped && <p class="muted small">It is daylight, so the sky is set to {fmtTime(jumped)} tonight.</p>}
      {body}
      <div class="tour-controls">
        <button class="btn-ghost" disabled={i === 0} onClick={() => setI(i - 1)} aria-label="Previous"><Icon name="back" size={16} /></button>
        <button class={canGo ? 'btn small' : 'btn-ghost small'} disabled={!canGo} onClick={next}>
          {last ? 'Finish' : 'find' in step && !found ? 'Skip' : 'Next'} <Icon name="forward" size={14} />
        </button>
      </div>
      <div class="tour-dots">{lesson.steps.map((_, k) => <span key={k} class={k === i ? 'on' : ''} />)}</div>
    </div>
  );
}

export function QuizPlayer() {
  const [qs, setQs] = useState<QuizQ[] | null>(null);
  const [jumped, setJumped] = useState<Date | null>(null);
  const [i, setI] = useState(0);
  const [picked, setPicked] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const [found, setFound] = useState(false);
  const [done, setDone] = useState(false);
  const q = qs?.[i];

  useEffect(() => {
    let alive = true;
    void darkFirst().then((t) => {
      if (!alive) return;
      setJumped(t);
      setQs(buildQuiz(quizHard.peek()));
    });
    return () => {
      alive = false;
      hideLabels.value = false;
      huntReticle.value = false;
      selection.value = null;
    };
  }, []);
  useEffect(() => {
    if (!q || done) return;
    setPicked(null);
    setFound(false);
    // Names stay hidden until the question is answered.
    hideLabels.value = q.kind === 'identify';
    huntReticle.value = q.kind === 'point';
    selection.value = q.kind === 'identify' || q.kind === 'point' ? q.sel : null;
    if (q.kind === 'identify') fly(q.enu, q.fov);
  }, [qs, i, done]);
  useFound(q?.kind === 'point' && !found && !done ? q.enu : null, q?.kind === 'point' ? q.radius : 4, () => {
    setFound(true);
    setScore((x) => x + 1);
  });

  const close = () => (panel.value = 'learn');
  if (!qs) {
    return (
      <div class="tour">
        <div class="working"><div class="spinner" /><p>Getting the sky ready…</p></div>
      </div>
    );
  }
  if (!qs.length || !q) {
    return (
      <div class="tour">
        <div class="tour-top"><span class="tour-step">Quiz</span><button class="icon-btn" aria-label="Close" onClick={close}><Icon name="close" size={18} /></button></div>
        <p>Too little is up to make a quiz right now. Try again once it is dark.</p>
      </div>
    );
  }
  const pick = (k: number) => {
    setPicked(k);
    hideLabels.value = false;
    if ('answer' in q && k === q.answer) setScore((x) => x + 1);
  };
  const next = () => {
    if (i < qs.length - 1) setI(i + 1);
    else {
      recordQuiz(score);
      hideLabels.value = false;
      huntReticle.value = false;
      selection.value = null;
      setDone(true);
    }
  };
  if (done) {
    const best = learnProgress.value.bestQuiz;
    return (
      <div class="tour quiz" role="dialog" aria-label="Quiz results">
        <div class="tour-top"><span class="tour-step">Quiz</span><button class="icon-btn" aria-label="Close" onClick={close}><Icon name="close" size={18} /></button></div>
        <h3>You scored {score} out of {qs.length}</h3>
        <p>{score === qs.length ? 'A perfect score. ' : score >= qs.length / 2 ? 'Well done. ' : 'A good start. '}The sky changes through the night and the seasons, so the questions will be different next time.{best > score ? ` Your best is ${best}.` : ''}</p>
        <div class="tour-controls">
          <button class="btn-ghost small" onClick={close}>Back to Learn</button>
          <button class="btn small" onClick={() => { panel.value = null; setTimeout(() => (panel.value = 'quiz'), 0); }}>Play again</button>
        </div>
      </div>
    );
  }
  const answered = q.kind === 'point' ? found : picked !== null;
  return (
    <div class="tour quiz" role="dialog" aria-label="Quiz">
      <div class="tour-top">
        <span class="tour-step tnum">Question {i + 1} of {qs.length} · score {score}</span>
        <button class="icon-btn" aria-label="End the quiz" onClick={close}><Icon name="close" size={18} /></button>
      </div>
      {i === 0 && jumped && <p class="muted small">It is daylight, so the sky is set to {fmtTime(jumped)} tonight.</p>}
      <h3>{q.kind === 'fact' ? `True or false? ${q.prompt}` : q.prompt}</h3>
      {q.kind === 'point' ? (
        <p aria-live="polite">{found ? `Found ${q.name}.` : viewMode.value === 'ar' ? 'Point your phone until it sits in the circle.' : 'Drag the sky until it sits in the circle.'}</p>
      ) : (
        <>
          <Choices options={q.options} answer={q.answer} picked={picked} onPick={pick} />
          {picked !== null && <p aria-live="polite">{picked === q.answer ? 'Right! ' : 'Not this time. '}{q.explain}</p>}
        </>
      )}
      <div class="tour-controls">
        <button class={answered ? 'btn small' : 'btn-ghost small'} onClick={next} disabled={q.kind !== 'point' && !answered}>
          {i < qs.length - 1 ? (q.kind === 'point' && !found ? 'Skip' : 'Next') : 'See your score'} <Icon name="forward" size={14} />
        </button>
      </div>
      <div class="tour-dots">{qs.map((_, k) => <span key={k} class={k === i ? 'on' : ''} />)}</div>
    </div>
  );
}

const fmtShort = (t: number) => new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(t);

/** The latest pictures and news from the observatories, and NASA's picture of the day. */
function Discover() {
  const [b, setB] = useState<NewsBundle | null | undefined>(undefined);
  useEffect(() => void loadNews().then(setB).catch(() => setB(null)), []);
  if (b === undefined) return <div class="muted small pad">Fetching the latest pictures…</div>;
  // Say why the pictures and news are missing, rather than leave a gap.
  if (!b) return <div class="muted small pad">{navigator.onLine === false ? 'The latest pictures and news need an internet connection.' : 'The latest pictures and news could not be fetched just now.'}</div>;
  return (
    <>
      <Reminder text={NEWS_REMINDER} />
      <h3 class="section-title">Latest pictures</h3>
      <div class="pic-strip">
        {b.pictures.map((p) => (
          <a class="pic-card" key={p.link} href={p.link} target="_blank" rel="noopener">
            {p.image && <img src={p.image} alt="" loading="lazy" />}
            <span class="pic-title">{p.title}</span>
            <span class="pic-src">{p.source} · {fmtShort(p.date)}</span>
          </a>
        ))}
      </div>
      {b.apod && (
        <a class="card wide apod" href={b.apod.link} target="_blank" rel="noopener">
          {b.apod.image && <img src={b.apod.image} alt="" loading="lazy" />}
          <div class="card-title"><Icon name="sparkle" size={16} /> NASA’s picture of the day</div>
          <div class="card-main">{b.apod.title}</div>
          <div class="card-sub">{b.apod.credit ? `By ${b.apod.credit}. ` : ''}{b.apod.image ? '' : 'Tap to see it on NASA’s site.'}</div>
        </a>
      )}
      <h3 class="section-title">News from the observatories</h3>
      <div class="results">
        {b.news.map((n) => (
          <a class="result" key={n.link} href={n.link} target="_blank" rel="noopener">
            <span class="result-text">
              <span class="result-label">{n.title}</span>
              <span class="result-sub">{n.source} · {fmtShort(n.date)}{n.summary ? ` · ${n.summary.length > 140 ? n.summary.slice(0, 140).replace(/\s+\S*$/, '') + '…' : n.summary}` : ''}</span>
            </span>
          </a>
        ))}
      </div>
      <p class="fine">Pictures and news: ESA/Webb, ESA/Hubble, ESO and NOIRLab, all <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener">CC BY 4.0</a> (full credits on each page); Astronomy Picture of the Day, NASA. Fetched from their feeds when you open Learn.</p>
    </>
  );
}

/** What this person has seen: counts, and the Messier objects to tick off. */
function SkyLog() {
  const seen = seenIndex.value;
  const log = Object.fromEntries([...seen].map(([k, v]) => [k, v.first]));
  const e = services.value!.engine;
  const keys = [...seen.keys()];
  const count = (prefix: string) => keys.filter((k) => k.startsWith(prefix)).length;
  const planets = PLANETS.filter((p) => log[`body:${p}`]).length;
  const messier = e.dsos.filter((d) => d.m).sort((a, b) => a.m! - b.m!);
  const seenM = messier.filter((d) => log[`dso:${d.id}`]).length;
  const open = (k: number) => {
    selection.value = { kind: 'dso', index: k };
    panel.value = null;
  };
  return (
    <div class="card wide">
      <div class="card-title"><Icon name="check" size={16} /> My sky log</div>
      <div class="card-sub">Tap “Seen it” on anything’s card once you have seen it for yourself. Each night is kept in Tonight › Your nights, on this device only.</div>
      <div class="log-stats">
        <div class="log-stat"><b>{planets}/7</b><span>planets</span></div>
        <div class="log-stat"><b>{count('con:')}/88</b><span>constellations</span></div>
        <div class="log-stat"><b>{seenM}/110</b><span>Messier</span></div>
        <div class="log-stat"><b>{count('star:') + count('sat:') + count('plane:') + count('shower:') + count('small:')}</b><span>other</span></div>
      </div>
      <details>
        <summary class="log-summary">The Messier list: {seenM ? `${seenM} seen` : 'none seen yet'}</summary>
        <div class="messier-grid">
          {messier.map((d) => {
            const k = e.dsos.indexOf(d);
            return <button key={d.id} class={log[`dso:${d.id}`] ? 'on' : ''} aria-label={`${d.id}${d.name ? `, ${d.name}` : ''}${log[`dso:${d.id}`] ? ', seen' : ''}`} onClick={() => open(k)}>{d.m}</button>;
          })}
        </div>
      </details>
    </div>
  );
}
